import { useEffect, useState } from 'react';
import { Plus, ArrowUp, ArrowDown, MessageSquareText } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import {
  adminList, adminCreate, adminUpdate, adminDelete, adminReorder,
} from '../../../lib/adminApi';
import ImagePicker from './ImagePicker';
import { SectionHeader, SaveBar, useSaver, ConfirmDelete, Field, Card } from './_ui';

export default function WhatsAppTestimonialsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const list = await adminList('whatsapp-testimonials');
    setItems(list || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const addItem = async () => {
    await adminCreate('whatsapp-testimonials', {
      image: '',
      alt: 'WhatsApp testimonial from a Science Sorted parent or student.',
      published: true,
      order: items.length,
    });
    await refresh();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await adminReorder('whatsapp-testimonials', next.map((r) => r.id));
  };

  return (
    <div className="max-w-4xl">
      <SectionHeader
        title="WhatsApp testimonials"
        description={
          "Screenshots shown in the \u201cReal messages. Real results.\u201d section. "
          + "Upload a message screenshot, write a short alt description for "
          + "accessibility, and reorder with the arrows. Only published items appear on the site."
        }
        actions={
          <Button
            onClick={addItem}
            className="bg-brand-sun text-brand-ink hover:brightness-95 rounded-full"
            data-testid="add-wa-testimonial"
          >
            <Plus className="w-4 h-4 mr-1" /> Add testimonial
          </Button>
        }
      />

      {loading ? (
        <p className="text-brand-ink/60">Loading testimonials...</p>
      ) : items.length === 0 ? (
        <Card>
          <div className="flex items-start gap-3">
            <MessageSquareText className="w-5 h-5 text-brand-coral mt-0.5" />
            <div>
              <p className="text-brand-ink font-medium">No testimonials yet.</p>
              <p className="text-brand-ink/70 text-sm mt-1">
                Click <strong>Add testimonial</strong> to upload your first WhatsApp screenshot.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((r, i) => (
            <WaRow
              key={r.id}
              item={r}
              index={i}
              total={items.length}
              onChange={(next) =>
                setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...next } : x)))
              }
              onSave={async (next) => {
                await adminUpdate('whatsapp-testimonials', r.id, next);
              }}
              onDelete={async () => {
                await adminDelete('whatsapp-testimonials', r.id);
                await refresh();
              }}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WaRow({ item, index, total, onChange, onSave, onDelete, onMoveUp, onMoveDown }) {
  const saver = useSaver();
  const local = item;
  const save = () => saver.run(() => onSave(local));

  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex flex-col shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"
            aria-label="Move up"
            data-testid={`wa-move-up-${index}`}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"
            aria-label="Move down"
            data-testid={`wa-move-down-${index}`}
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-w-0 space-y-4">
          <ImagePicker
            label="Screenshot"
            size="lg"
            value={local.image || ''}
            onChange={(url) => onChange({ image: url })}
          />

          <Field label="Alt text (screen-reader description)" hint="Briefly summarise the message, e.g. 'Parent thanks tutor for son's A* in biology.'">
            <Input
              value={local.alt || ''}
              onChange={(e) => onChange({ alt: e.target.value })}
              data-testid={`wa-alt-${index}`}
            />
          </Field>

          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch
                checked={local.published !== false}
                onCheckedChange={(v) => onChange({ published: v })}
              />
              Published
            </label>
            <span className="text-xs text-brand-ink/50">Position: {index + 1} of {total}</span>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-brand-ink/10">
            <ConfirmDelete onConfirm={onDelete} />
            <SaveBar {...saver} onSave={save} dataTestId={`save-wa-${index}`} />
          </div>
        </div>
      </div>
    </Card>
  );
}
